import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import CustomCursor from '../components/CustomCursor';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Play, Image as ImageIcon, X, ChevronRight, MapPin } from 'lucide-react';

const InternationalTrip = () => {
  const [darkMode, setDarkMode] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const [activeEventIndex, setActiveEventIndex] = useState(null);
  const [showImage, setShowImage] = useState(false);
  const [popupImageUrl, setPopupImageUrl] = useState('');
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    document.documentElement.classList.toggle('dark');
  };

  const getEmbedUrl = (url) => {
    if (!url) return '';
    if (url.includes('/embed/')) return url;
    const watchMatch = url.match(/(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?v=([\w-]+)/);
    if (watchMatch) return `https://www.youtube.com/embed/${watchMatch[1]}`;
    const shortsMatch = url.match(/(?:https?:\/\/)?(?:www\.)?youtube\.com\/shorts\/([\w-]+)/);
    if (shortsMatch) return `https://www.youtube.com/embed/${shortsMatch[1]}`;
    const shortMatch = url.match(/youtu\.be\/([\w-]+)/);
    if (shortMatch) return `https://www.youtube.com/embed/${shortMatch[1]}`;
    return url;
  };

  const openVideo = (url) => {
    setVideoUrl(getEmbedUrl(url));
    setShowVideo(true);
    setActiveEventIndex(null);
  };

  const openImage = (imgUrl) => {
    setPopupImageUrl(imgUrl);
    setShowImage(true);
    setActiveEventIndex(null);
  };

  const foreignEvents = [
    {
      name: 'Europe Diaries',
      date: 'Jan 2026',
      location: 'Europe',
      image: '/assest/Events/germany.jpeg',
      videoUrls: [
        { url: 'https://www.youtube.com/shorts/hysZ5s7H5dw', title: 'Austria Day 4' },
        { url: 'https://www.youtube.com/shorts/oS23cm_bn24', title: 'Germany & Austria Day 3' },
        { url: 'https://www.youtube.com/shorts/qqLC2INWPME', title: 'Germany Day 2' },
        { url: 'https://www.youtube.com/shorts/ADHYL2Qy3QU', title: 'Germany Day 1' },
      ],
    },
    {
      name: 'Town Hall - Thailand',
      date: '2024 & 2025',
      location: 'Thailand',
      image: '/assest/Events/nice.jpeg',
      videoUrls: [
        { url: 'https://www.youtube.com/watch?v=AF-MMlHdKyU', title: 'Town Hall 2K25 — May 29, 2025' },
        { url: 'https://www.youtube.com/watch?v=yZkFhicdQmA', title: 'Town Hall 2K24 — Feb 01, 2024' },
      ],
    },
    {
      name: 'Tour De Thai - Thailand',
      date: 'May 27, 2025',
      location: 'Thailand',
      image: '/assest/Events/tourde.jpeg',
      videoUrls: [
        { url: 'https://www.youtube.com/shorts/yIwp85h6noA', title: 'Tour De Thai — Part 1' },
        { url: 'https://www.youtube.com/shorts/LYNZU_4siD4', title: 'Tour De Thai — Part 2' },
      ],
    },
    {
      name: 'Fly Thai - Thailand Trip',
      date: 'Jan 29 — Feb 2, 2024',
      location: 'Thailand',
      image: '/assest/Events/thailand.jpeg',
      videoUrl: 'https://www.youtube.com/watch?v=8hWFFqkO9kE',
    },
    {
      name: 'Azerbaijan Baku',
      date: 'Oct 1 - 2023',
      location: 'Azerbaijan',
      image: '/assest/Events/azerbaijan.jpeg',
      videoUrl: 'https://www.youtube.com/watch?v=7sPrP9hQ4Ng',
    },
    {
      name: 'Qatar & Dubai Trip',
      date: '2022',
      location: 'Qatar & Dubai',
      image: '/assest/Events/dubai x qatar.jpeg',
      videoUrl: 'https://www.youtube.com/watch?v=uKAghhmfZms',
    },
    {
      name: 'IndusViva US Launch',
      date: '2019',
      location: 'USA',
      image: '/assest/Events/usa.jpeg',
      videoUrl: 'https://www.youtube.com/watch?v=_I3D6emYbtE',
    },
    {
      name: 'Pearl Premier',
      date: '2019',
      location: 'Malaysia',
      image: '/assest/Events/malaysia.jpeg',
      videoUrl: 'https://www.youtube.com/watch?v=JUMWnqlCDT8',
    },
    {
      name: 'Italy Trip',
      date: 'Nov 2018',
      location: 'Italy',
      image: '/assest/Events/italy.jpeg',
      videoUrl: 'https://www.youtube.com/watch?v=OeKIet_GY5M',
    },
    {
      name: 'Ruby Fly-In - Thailand Trip',
      date: 'July 2018',
      location: 'Thailand',
      image: '/assest/Events/thailand.jpeg',
      videoUrl: 'https://www.youtube.com/watch?v=ss7hcesBOak',
    },
    {
      name: 'Dubai Trip',
      date: '2017',
      location: 'Dubai',
      image: '/assest/Events/dubai.jpeg',
      videoUrl: 'https://www.youtube.com/watch?v=5ZeChnxO9Xs',
    },
    {
      name: 'Singapore Trip',
      date: '2016',
      location: 'Singapore',
      image: '/assest/Events/singapore.jpeg',
      videoUrl: 'https://www.youtube.com/watch?v=DzHQOha1Gsg',
    },
  ];

  const silk = [0.25, 0.46, 0.45, 0.94];
  const expo = [0.16, 1, 0.3, 1];

  const eyebrowV = {
    hidden: { opacity: 0, y: 14 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: silk } },
  };
  const wordVariants = {
    hidden: { opacity: 0, y: 44, rotateX: 14 },
    visible: { opacity: 1, y: 0, rotateX: 0, transition: { duration: 0.8, ease: expo } },
  };
  const italicVariants = {
    hidden: { opacity: 0, x: -36, skewX: -5 },
    visible: { opacity: 1, x: 0, skewX: 0, transition: { duration: 0.7, ease: expo } },
  };
  const btnVariant = {
    hidden: { opacity: 0, y: 14 },
    visible: (i) => ({
      opacity: 1, y: 0,
      transition: { duration: 0.9, ease: silk, delay: 0.5 + i * 0.14 },
    }),
  };
  const indexNumVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.9, ease: silk } },
  };
  const indexLineVariants = {
    hidden: { scaleX: 0, opacity: 0 },
    visible: { scaleX: 1, opacity: 1, transition: { duration: 1.1, ease: expo } },
  };
  const locationVariants = {
    hidden: { opacity: 0, x: -12 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.7, ease: silk, delay: 0.15 } },
  };
  const cardVariants = {
    hidden: { opacity: 0, y: 56, scale: 0.97 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 1.3, ease: expo } },
  };
  const footLineLeft  = { hidden: { scaleX: 0, opacity: 0 }, visible: { scaleX: 1, opacity: 1, transition: { duration: 1.2, ease: expo } } };
  const footLineRight = { hidden: { scaleX: 0, opacity: 0 }, visible: { scaleX: 1, opacity: 1, transition: { duration: 1.2, ease: expo } } };
  const footLabelV    = { hidden: { opacity: 0, y: 6 }, visible: { opacity: 1, y: 0, transition: { duration: 1.0, ease: silk, delay: 0.2 } } };

  return (
    <div className={darkMode ? 'dark' : ''}>
      <style dangerouslySetInnerHTML={{ __html: `
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&family=Outfit:wght@300;400;500;600&display=swap');

        * { box-sizing: border-box; }
        body { background: #0f1e38; }

        .foreign-hero {
          background: #0f1e38;
          padding-bottom: 0;
          padding-top: 20px;
          position: relative;
          z-index: 1;
        }

        .foreign-body {
          background: #ffffff;
          position: relative;
          z-index: 1;
        }

        .page-heading {
          font-family: 'Cormorant Garamond', serif;
          font-weight: 500;
          color: #E8F2FA;
          letter-spacing: -0.015em;
          line-height: 1.15;
          perspective: 700px;
        }
        .page-heading-italic {
          font-family: 'Cormorant Garamond', serif;
          font-style: italic;
          font-weight: 400;
          color: rgb(59, 130, 246);
          display: block;
          margin-top: 4px;
        }

        .section-eyebrow {
          font-family: 'Outfit', sans-serif;
          font-size: 0.6rem; font-weight: 500;
          letter-spacing: 0.3em; text-transform: uppercase;
          color: #8fafd4;
          display: flex; align-items: center; gap: 12px;
        }
        .eyebrow-line {
          display: inline-block; width: 32px; height: 1px;
          background: rgb(59, 130, 246);
        }

        .hero-rule {
          height: 1px;
          background: linear-gradient(to right, rgba(59,130,246,0.5), transparent);
          margin-top: 20px;
        }

        .nav-pill {
          display: inline-flex; align-items: center; gap: 7px;
          font-family: 'Outfit', sans-serif; font-size: 0.68rem; font-weight: 500;
          letter-spacing: 0.08em; text-transform: uppercase; text-decoration: none;
          padding: 10px 18px; border-radius: 100px; white-space: nowrap;
          transition: background 0.5s ease, border-color 0.5s ease, color 0.5s ease,
                      transform 0.5s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.5s ease;
          cursor: pointer;
        }
        .nav-pill--ghost {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(140,200,240,0.18);
          color: rgba(180,220,245,0.7);
        }
        .nav-pill--ghost:hover {
          background: rgba(255,255,255,0.09);
          border-color: rgba(140,200,240,0.45);
          color: #C8E8FA; transform: translateX(-4px);
        }
        .nav-pill--solid {
          background: linear-gradient(135deg, #1A4A70 0%, #0F2D50 100%);
          border: 1px solid rgba(100,180,230,0.3);
          color: #B8DEFA;
          box-shadow: 0 4px 18px rgba(0,30,80,0.35);
        }
        .nav-pill--solid:hover {
          background: linear-gradient(135deg, #2060A0 0%, #163D70 100%);
          border-color: rgba(140,210,250,0.55);
          color: #E0F4FF;
          box-shadow: 0 8px 28px rgba(0,40,100,0.45);
          transform: translateX(4px);
        }

        .index-num {
          font-family: 'Cormorant Garamond', serif;
          font-style: italic; font-size: 0.8rem; color: #6b8cae;
          flex-shrink: 0;
        }
        .index-hr {
          flex: 1; height: 1px;
          background: rgba(37,99,235,0.12);
          transform-origin: left center;
        }

        .location-strip {
          display: flex;
          align-items: center;
          gap: 9px;
          height: 44px;
          margin: 6px 0 10px;
          padding: 0 14px;
          background: rgba(37,99,235,0.04);
          border: 1px solid rgba(37,99,235,0.12);
          border-radius: 8px;
        }
        .location-strip-icon {
          display: flex;
          align-items: center;
          color: rgb(59,130,246);
          flex-shrink: 0;
        }
        .location-strip-text {
          font-family: 'Outfit', sans-serif;
          font-size: 0.88rem;
          font-weight: 600;
          letter-spacing: 0.02em;
          color: #0f172a;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .event-card {
          position: relative;
          overflow: hidden;
          border-radius: 14px;
          border: 1px solid rgba(37,99,235,0.12);
          background: rgba(10,25,50,0.65);
          backdrop-filter: blur(14px);
          -webkit-backdrop-filter: blur(14px);
          cursor: pointer;
          height: 400px;
          transition: border-color 0.6s ease, box-shadow 0.6s ease,
                      transform 0.6s cubic-bezier(0.25,0.46,0.45,0.94);
        }
        .event-card:hover {
          border-color: rgba(59,130,246,0.35);
          box-shadow: 0 24px 60px rgba(37,99,235,0.15);
          transform: translateY(-5px);
        }

        .card-img {
          position: absolute; inset: 0;
          width: 100%; height: 100%;
          object-fit: cover;
          transform: scale(1.06);
          transition: transform 2.6s cubic-bezier(0.16,1,0.3,1), filter 0.8s ease;
        }
        .event-card:hover .card-img { transform: scale(1.1); filter: brightness(0.9); }

        .card-placeholder {
          position: absolute; inset: 0;
          background: linear-gradient(145deg, #0D1D45 0%, #0F2D60 50%, #1A3F80 100%);
        }
        .placeholder-pattern {
          position: absolute; inset: 0;
          background-image: radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px);
          background-size: 24px 24px;
        }

        .card-overlay {
          position: absolute; inset: 0;
          background: linear-gradient(
            to bottom,
            rgba(4,10,24,0.80) 0%,
            rgba(4,10,24,0.06) 45%,
            transparent 100%
          );
          z-index: 1;
        }
        .card-shine {
          position: absolute; bottom: 0; left: 0; right: 0; height: 1px;
          background: linear-gradient(to right, transparent, rgba(59,130,246,0.6), transparent);
          opacity: 0; z-index: 2;
          transition: opacity 0.4s ease;
        }
        .event-card:hover .card-shine { opacity: 1; }

        .card-body {
          position: absolute; inset: 0; z-index: 3;
          padding: 22px 22px 20px;
          display: flex; flex-direction: column; justify-content: space-between;
        }
        .card-top { display: flex; flex-direction: column; }

        .card-date {
          font-family: 'Outfit', sans-serif; font-size: 0.58rem;
          letter-spacing: 0.24em; text-transform: uppercase;
          color: rgba(255,255,255,0.9); margin-bottom: 6px;
        }
        .card-sep {
          height: 1px; width: 28px;
          background: rgba(255,255,255,0.6);
          margin-bottom: 10px;
          transition: width 0.6s cubic-bezier(0.25,0.46,0.45,0.94);
        }
        .event-card:hover .card-sep { width: 48px; }

        .card-name {
          font-family: 'Cormorant Garamond', serif;
          font-weight: 500; font-size: 1.65rem;
          line-height: 1.1; letter-spacing: -0.02em; color: #EEF4FA;
          transition: letter-spacing 0.6s ease;
        }
        .event-card:hover .card-name { letter-spacing: -0.015em; }

        .card-footer {
          display: flex; align-items: center; justify-content: space-between;
        }
        .card-cta-label {
          font-family: 'Outfit', sans-serif; font-size: 0.62rem;
          letter-spacing: 0.2em; text-transform: uppercase;
          color: rgba(255,255,255,0.85);
          text-shadow: 0 1px 6px rgba(0,0,0,0.6);
          transition: color 0.5s ease, letter-spacing 0.5s ease;
        }
        .event-card:hover .card-cta-label { color: #ffffff; letter-spacing: 0.26em; }

        .card-action-group { display: flex; align-items: center; gap: 8px; }

        .action-btn {
          width: 38px; height: 38px; border-radius: 50%;
          border: 1.5px solid rgba(255,255,255,0.75);
          background: rgba(255,255,255,0.18);
          backdrop-filter: blur(8px);
          display: flex; align-items: center; justify-content: center;
          cursor: pointer; color: #ffffff;
          transition: background 0.3s ease, border-color 0.3s ease,
                      transform 0.4s cubic-bezier(0.34,1.56,0.64,1);
        }
        .action-btn:hover {
          background: rgba(255,255,255,0.35);
          border-color: #ffffff;
          transform: scale(1.1);
        }

        .video-menu {
          position: absolute; bottom: calc(100% + 8px); right: 0;
          min-width: 220px;
          background: rgba(10,22,45,0.96);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(59,130,246,0.2);
          border-radius: 10px; overflow: hidden; z-index: 50;
          box-shadow: 0 16px 48px rgba(0,10,30,0.6);
        }
        .video-menu-item {
          width: 100%; padding: 11px 16px; text-align: left;
          font-family: 'Outfit', sans-serif; font-size: 0.72rem;
          color: rgba(200,225,245,0.75); letter-spacing: 0.04em;
          display: flex; align-items: center; gap: 10px;
          border-bottom: 1px solid rgba(255,255,255,0.05);
          transition: background 0.2s ease, color 0.2s ease;
          background: transparent; cursor: pointer;
        }
        .video-menu-item:last-child { border-bottom: none; }
        .video-menu-item:hover { background: rgba(59,130,246,0.12); color: #E8F4FC; }

        .foot-hr { flex: 1; height: 1px; background: rgba(37,99,235,0.1); }
        .foot-hr-left  { transform-origin: left center; }
        .foot-hr-right { transform-origin: right center; }
        .foot-label {
          font-family: 'Outfit', sans-serif; font-size: 0.55rem;
          letter-spacing: 0.3em; text-transform: uppercase;
          color: rgba(37,99,235,0.35);
          white-space: nowrap;
        }

        .modal-backdrop {
          position: fixed; inset: 0; z-index: 9999;
          background: rgba(0,0,0,0.85); backdrop-filter: blur(8px);
          display: flex; align-items: center; justify-content: center; padding: 20px;
        }
        .modal-close {
          position: absolute; top: 14px; right: 14px;
          background: rgba(0,0,0,0.5);
          border: 1px solid rgba(255,255,255,0.15);
          border-radius: 50%; width: 36px; height: 36px;
          display: flex; align-items: center; justify-content: center;
          color: white; cursor: pointer; transition: background 0.3s;
        }
        .modal-close:hover { background: rgba(0,0,0,0.8); }

        .events-grid {
          display: grid;
          gap: 28px;
          grid-template-columns: repeat(auto-fill, minmax(min(100%, 300px), 1fr));
        }

        @media (max-width: 640px) {
          .events-grid { gap: 20px; }
          .card-body { padding: 16px 16px 14px; }
        }

        @media (min-width: 1280px) {
          .events-grid { gap: 32px; }
        }
      `}} />

      <CustomCursor />

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      {/* ── HERO BANNER ── */}
      <div className="foreign-hero">
        <div ref={ref} className="container mx-auto px-6 max-w-7xl">
          <div className="mt-24 mb-0 pb-16">
            <motion.p
              className="section-eyebrow mb-5 mt-16"
              variants={eyebrowV} initial="hidden" animate={inView ? 'visible' : 'hidden'}
            >
              <span className="eyebrow-line" />
              Our Programmes
            </motion.p>

            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
              {/* Desktop heading */}
              <div className="hidden md:flex items-end gap-7">
                <motion.h1
                  className="page-heading text-4xl md:text-5xl"
                  variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.12, delayChildren: 0.1 } } }}
                  initial="hidden" animate={inView ? 'visible' : 'hidden'}
                >
                  <span style={{ display: 'flex', flexWrap: 'wrap', gap: '0 0.3em' }}>
                    {['International', 'Events'].map((word) => (
                      <motion.span key={word} variants={wordVariants} style={{ display: 'inline-block' }}>{word}</motion.span>
                    ))}
                  </span>
                  <motion.span className="page-heading-italic" variants={italicVariants}>Global Journey</motion.span>
                </motion.h1>

                <motion.div
                  className="block"
                  style={{
                    width: 1, alignSelf: 'stretch',
                    background: 'linear-gradient(to bottom, transparent, rgba(59,130,246,0.5), transparent)',
                    marginBottom: 4,
                  }}
                  initial={{ opacity: 0, scaleY: 0.5 }}
                  animate={inView ? { opacity: 1, scaleY: 1 } : {}}
                  transition={{ duration: 0.7, ease: expo, delay: 0.4 }}
                />

                <motion.p
                  className="block"
                  style={{
                    fontFamily: "'Outfit', sans-serif", fontSize: '0.82rem', fontWeight: 300,
                    color: 'rgba(200,220,255,0.7)', lineHeight: 1.75, maxWidth: 380, marginBottom: 4,
                  }}
                  initial={{ opacity: 0, y: 18 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.9, ease: expo, delay: 0.45 }}
                >
                  From Thailand's grand arenas to exclusive fly-in retreats, celebrating global milestones together.
                </motion.p>
              </div>

              {/* Mobile heading */}
              <div className="md:hidden flex flex-col items-start gap-4 flex-1">
                <motion.h1
                  className="page-heading text-4xl"
                  variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.12, delayChildren: 0.1 } } }}
                  initial="hidden" animate={inView ? 'visible' : 'hidden'}
                >
                  <span style={{ display: 'flex', flexWrap: 'wrap', gap: '0 0.3em' }}>
                    {['International', 'Events'].map((word) => (
                      <motion.span key={word} variants={wordVariants} style={{ display: 'inline-block' }}>{word}</motion.span>
                    ))}
                  </span>
                  <motion.span className="page-heading-italic" variants={italicVariants}>Global Journey</motion.span>
                </motion.h1>
                <motion.p
                  style={{
                    fontFamily: "'Outfit', sans-serif", fontSize: '0.82rem', fontWeight: 300,
                    color: 'rgba(200,220,255,0.7)', lineHeight: 1.75, maxWidth: 380, marginBottom: 4,
                  }}
                  initial={{ opacity: 0, y: 18 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.9, ease: expo, delay: 0.45 }}
                >
                  From Thailand's grand arenas to exclusive fly-in retreats, celebrating global milestones together.
                </motion.p>
              </div>

              {/* Nav pills */}
              <div className="flex flex-wrap items-center gap-3 flex-shrink-0 mt-4 md:mt-0">
                {[
                  { href: '/events', label: 'Back to Events', cls: 'nav-pill--ghost', arrow: 'left' },
                  { href: '/indian-event', label: 'National Events', cls: 'nav-pill--solid', arrow: 'right' },
                ].map((btn, i) => (
                  <motion.a
                    key={btn.label} href={btn.href} className={`nav-pill ${btn.cls}`}
                    custom={i} variants={btnVariant} initial="hidden" animate={inView ? 'visible' : 'hidden'}
                  >
                    {btn.arrow === 'left' && (
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
                        <path d="M9 11.5L4.5 7L9 2.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                    {btn.label}
                    {btn.arrow === 'right' && (
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
                        <path d="M5 2.5L9.5 7L5 11.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </motion.a>
                ))}
              </div>
            </div>

            {/* Hero bottom rule */}
            <motion.div
              className="hero-rule"
              initial={{ opacity: 0 }}
              animate={inView ? { opacity: 1 } : {}}
              transition={{ duration: 0.9, ease: expo, delay: 0.55 }}
            />
          </div>
        </div>
      </div>

      {/* ── CARDS BODY ── */}
      <div className="foreign-body py-16">
        <div className="container mx-auto px-6 max-w-7xl">

          <motion.div
            className="events-grid"
            variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.1, delayChildren: 0.45 } } }}
            initial="hidden" animate={inView ? 'visible' : 'hidden'}
          >
            {foreignEvents.map((event, index) => (
              <motion.div key={index} variants={cardVariants}>

                {/* Index row */}
                <motion.div
                  className="flex items-center gap-3 mb-0"
                  variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.1 } } }}
                >
                  <motion.span className="index-num" variants={indexNumVariants}>
                    {String(index + 1).padStart(2, '0')}
                  </motion.span>
                  <motion.div className="index-hr" variants={indexLineVariants} />
                </motion.div>

                {/* Location strip */}
                <motion.div className="location-strip" variants={locationVariants}>
                  <span className="location-strip-icon">
                    <MapPin size={14} strokeWidth={2} />
                  </span>
                  <span className="location-strip-text">
                    {event.date && `${event.date} — `}{event.location}
                  </span>
                </motion.div>

                {/* ── CARD with full click handler ── */}
                <div
                  className="event-card"
                  onClick={() => {
                    if (event.popupImage) {
                      openImage(event.popupImage);
                    } else if (event.videoUrls) {
                      setActiveEventIndex(activeEventIndex === index ? null : index);
                    } else if (event.videoUrl) {
                      openVideo(event.videoUrl);
                    }
                  }}
                >
                  {event.image ? (
                    <img className="card-img" src={event.image} alt={event.name} />
                  ) : (
                    <div className="card-placeholder">
                      <div className="placeholder-pattern" />
                    </div>
                  )}

                  <div className="card-overlay" />
                  <div className="card-shine" />

                  <div className="card-body">
                    <div className="card-top">
                      <h3 className="card-name">{event.name}</h3>
                    </div>

                    <div className="card-footer">
                      <span className="card-cta-label">Explore Event</span>

                      <div className="card-action-group">
                        <div className="relative">
                          {event.popupImage ? (
                            <button
                              className="action-btn"
                              onClick={(e) => { e.stopPropagation(); openImage(event.popupImage); }}
                              title="View Photo"
                            >
                              <ImageIcon size={14} strokeWidth={1.5} />
                            </button>
                          ) : event.videoUrls ? (
                            <>
                              <button
                                className="action-btn"
                                onClick={(e) => { e.stopPropagation(); setActiveEventIndex(activeEventIndex === index ? null : index); }}
                                title="Watch Videos"
                              >
                                <Play size={13} strokeWidth={1.5} style={{ marginLeft: 1 }} />
                              </button>
                              {activeEventIndex === index && (
                                <div className="video-menu" onClick={(e) => e.stopPropagation()}>
                                  {event.videoUrls.map((v, vi) => (
                                    <button
                                      key={vi}
                                      className="video-menu-item"
                                      onClick={(e) => { e.stopPropagation(); openVideo(v.url); }}
                                    >
                                      <Play size={11} strokeWidth={1.5} style={{ flexShrink: 0 }} />
                                      {v.title}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </>
                          ) : event.videoUrl ? (
                            <button
                              className="action-btn"
                              onClick={(e) => { e.stopPropagation(); openVideo(event.videoUrl); }}
                              title="Watch Video"
                            >
                              <Play size={13} strokeWidth={1.5} style={{ marginLeft: 1 }} />
                            </button>
                          ) : (
                            <div className="action-btn">
                              <ChevronRight size={14} strokeWidth={1.5} />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

              </motion.div>
            ))}
          </motion.div>

          {/* Footer rule */}
          <motion.div
            className="mt-14 flex items-center gap-4"
            variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.08, delayChildren: 1.2 } } }}
            initial="hidden" animate={inView ? 'visible' : 'hidden'}
          >
            <motion.div className="foot-hr foot-hr-left" variants={footLineLeft} />
            <motion.span className="foot-label" variants={footLabelV}>International Programmes</motion.span>
            <motion.div className="foot-hr foot-hr-right" variants={footLineRight} />
          </motion.div>

        </div>
      </div>

      {/* Video Modal */}
      {showVideo && (
        <div className="modal-backdrop" onClick={() => setShowVideo(false)}>
          <div
            className="relative w-full max-w-4xl rounded-2xl overflow-hidden bg-black"
            style={{ aspectRatio: '16/9' }}
            onClick={(e) => e.stopPropagation()}
          >
            <button className="modal-close" onClick={() => setShowVideo(false)}>
              <X size={16} />
            </button>
            <iframe
              width="100%" height="100%"
              src={videoUrl ? `${videoUrl}?autoplay=1` : ''}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-full"
            />
          </div>
        </div>
      )}

      {/* Image Modal */}
      {showImage && (
        <div className="modal-backdrop" onClick={() => setShowImage(false)}>
          <div
            className="relative w-full max-w-3xl rounded-2xl overflow-hidden bg-black"
            style={{ aspectRatio: '16/9' }}
            onClick={(e) => e.stopPropagation()}
          >
            <button className="modal-close" onClick={() => setShowImage(false)}>
              <X size={16} />
            </button>
            <img
              src={popupImageUrl}
              alt="Event"
              className="w-full h-full object-contain bg-black"
              style={{ maxHeight: '80vh' }}
            />
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default InternationalTrip;